import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/app/store';
import { Supplier, SupplierState } from './suppliers.interface';
import * as thunks from './suppliersThunks';
import { APIResponse } from '@/types/custom-http';


const initialState: SupplierState = {
  items: [],
  selectedSupplier: null,
  loading: false,
  error: null,
};



// Slice
const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    clearSuppliers: (state) => {
      state.items = [];
    },
    clearSelectedSupplier: (state) => {
      state.selectedSupplier = null;
    },
    clearSupplierError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Obtener todos los proveedores
      .addCase(thunks.fetchSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchSuppliers.fulfilled, (state, action: PayloadAction<APIResponse<Supplier[]>>) => {
        state.loading = false;
        if(action.payload.data){
          state.items = action.payload.data;
        }
      })
      .addCase(thunks.fetchSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Obtener un proveedor específico por ID
      .addCase(thunks.fetchSupplierById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchSupplierById.fulfilled, (state, action: PayloadAction<APIResponse<Supplier>>) => {
        state.loading = false;
        if(action.payload.data){
          state.selectedSupplier = action.payload.data;
        }
      })
      .addCase(thunks.fetchSupplierById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Crear un nuevo proveedor
      .addCase(thunks.createSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createSupplier.fulfilled, (state, action: PayloadAction<APIResponse<Supplier>>) => {
        state.loading = false;
        if(action.payload.data){
          state.items.push(action.payload.data);
        }
      })
      .addCase(thunks.createSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Actualizar datos de un proveedor
      .addCase(thunks.updateSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateSupplier.fulfilled, (state, action: PayloadAction<APIResponse<Supplier>>) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.data?.id);
        if (index !== -1 && action.payload.data) {
          state.items[index] = action.payload.data;
        }
        if (state.selectedSupplier && state.selectedSupplier.id === action.payload.data?.id) {
          state.selectedSupplier = action.payload.data;
        }
      })
      .addCase(thunks.updateSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })
      
  },
});

// Selectors
export const selectAllSuppliers = (state: RootState) => state.suppliers.items;
export const selectSupplierById = (state: RootState, id: number) => state.suppliers.items.find(item => item.id === id);
export const selectSelectedSupplier = (state: RootState) => state.suppliers.selectedSupplier;
export const selectSupplierLoading = (state: RootState) => state.suppliers.loading;
export const selectSupplierError = (state: RootState) => state.suppliers.error;

// Actions
export const { clearSuppliers, clearSelectedSupplier,  clearSupplierError} = suppliersSlice.actions;

export default suppliersSlice.reducer;